import React, { forwardRef, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import InputMask, { ReactInputMask } from 'react-input-mask';
import InfoTooltip from '@uikit/Info/InfoTooltip/InfoTooltip';
import { ControlledPhoneInputProps } from '@uikit/FormItems/constants';
import { getDynamicValidationColorsGlobal } from '@uikit/FormItems/ControlledItems/helpers';
import { useLazyCheckPhoneNumberQuery } from '@store/rtk/twilio';
import { toast } from 'react-toastify';
import { AsteriskOutlinedIcon, PhoneIcon, UserPhoneIcon } from '@src/svg';
import { logger } from '@utils/logger';
import { toastError } from '@utils/helpers';
import { toastMessage } from '@utils/toastMessage';

export const ControlledPhoneInput = forwardRef<
  HTMLInputElement,
  ControlledPhoneInputProps
>(
  (
    {
      name,
      control,
      rules,
      title,
      placeholder,
      maxWidth,
      disabled,
      info,
      hasLeftIcon,
      hasRightIcon = true,
      min,
      max,
      mask,
      onBlur,
      tabIndex,
      watch,
      onChange,
      setError: setErrorProp,
      setValue,
      clearErrors: clearErrorsProp,
      typeKey,
      dataTestId,
      hideErrorWhenEmpty = false,
      required = false,
    },
    ref
  ) => {
    const inputRef = useRef<ReactInputMask>(null);
    const isMobileRef = useRef<string>(watch?.(typeKey) ?? 'mobile');
    const isMobilePhoneRef = useRef<boolean>(watch?.(typeKey) === 'mobile');

    const [isLookupInvalid, setIsLookupInvalid] = useState<boolean>(false);

    const formCtx = (() => {
      try {
        return useFormContext();
      } catch {
        return undefined;
      }
    })();
    const setFormError = setErrorProp || formCtx?.setError;
    const clearFormErrors = clearErrorsProp || formCtx?.clearErrors;

    const [triggerPhoneLookup] = useLazyCheckPhoneNumberQuery();
    const lastCheckedPhoneRef = useRef<string>('');

    const validatePhoneIfNeeded = async (rawValue: string) => {
      const isPhoneMask = mask === '(999) 999-9999';
      const digitsOnly = (rawValue || '').replace(/\D/g, '');
      if (!isPhoneMask) return;
      if (digitsOnly.length !== 10) return;
      if (lastCheckedPhoneRef.current === digitsOnly) return;
      lastCheckedPhoneRef.current = digitsOnly;
      try {
        const res: any = await triggerPhoneLookup(digitsOnly).unwrap();
        const isValid = res?.data?.valid;
        if (isValid === false) {
          toast.error(toastMessage.COMMON.itsNotAValidNumber);
          setIsLookupInvalid(true);
          setFormError?.(
            name as any,
            {
              type: 'validate',
              message: "It's not a valid number",
            } as any
          );
        } else {
          setIsLookupInvalid(false);
          clearFormErrors?.(name as any);
        }
        isMobilePhoneRef.current =
          res?.data?.lineTypeIntelligence?.type === 'mobile';
        isMobileRef.current = res?.data?.lineTypeIntelligence?.type;
      } catch (e) {
        toastError(e, toastMessage.COMMON.failedToCheckTheNumber);
        logger.log(e, 'error');
      }
    };

    return (
      <div style={{ maxWidth }} className="input-text">
        {info?.toggle && (
          <InfoTooltip className="info_shower" title={info?.desc}>
            <div></div>
          </InfoTooltip>
        )}
        <div className={!disabled ? '' : 'disabled'}>
          <div className="input-title-wrapper">
            {title && (
              <span style={{ whiteSpace: 'nowrap' }} className="input-title">
                {title}
                {required && (
                  <span className="input-required-marker" aria-hidden="true">
                    <AsteriskOutlinedIcon
                      width={10}
                      height={10}
                      color="#fb6403"
                    />
                  </span>
                )}
              </span>
            )}
          </div>

          <Controller
            name={name}
            control={control}
            rules={{
              ...rules,
              min: min
                ? { value: min, message: `minimum value is: ${min}` }
                : undefined,
              max: max
                ? { value: max, message: `maximum value is: ${max}` }
                : undefined,
            }}
            render={({ field, fieldState }) => {
              const hasValue = String(field.value ?? '').length > 0;
              const baseInvalid = rules?.validate
                ? typeof rules?.validate === 'function'
                  ? !(rules?.validate as unknown as any)?.()
                  : !!(rules?.validate as unknown as any)?.exactLength?.(
                      field.value
                    )
                : false;

              const isInvalid =
                baseInvalid || isLookupInvalid || Boolean(fieldState?.error);

              const shouldShowValidation = !hideErrorWhenEmpty || hasValue;
              const validationDynamicClassName =
                getDynamicValidationColorsGlobal(
                  field,
                  shouldShowValidation && isInvalid
                );

              return (
                <span className={`input  ${validationDynamicClassName}`}>
                  {hasLeftIcon &&
                    (isMobilePhoneRef.current ||
                    watch?.(typeKey) === 'mobile' ? (
                      <UserPhoneIcon />
                    ) : (
                      <PhoneIcon variant="profile" />
                    ))}
                  {mask && (
                    <InputMask
                      data-test-id={dataTestId}
                      autoComplete={'off'}
                      {...field}
                      id={name}
                      mask={mask}
                      placeholder={placeholder}
                      disabled={disabled}
                      ref={inputRef}
                      {...(tabIndex ? { tabIndex } : {})}
                      onChange={async (e) => {
                        const value = e.target.value
                          .replace(/[_():\s-]/g, '')
                          .trim();
                        field.onChange({ target: { value } });
                        await validatePhoneIfNeeded(value);
                        if (onChange) onChange(name, value);
                        setValue(typeKey, isMobileRef.current ?? 'landline', {
                          shouldDirty: true,
                        });
                      }}
                      onBlur={(e) => {
                        const value = e.target.value
                          .replace(/[_():\s-]/g, '')
                          .trim();

                        field.onBlur();
                        if (onBlur) onBlur({ target: { value } });
                      }}
                    />
                  )}
                  {hasRightIcon &&
                    (isMobilePhoneRef.current ||
                    watch?.(typeKey) === 'mobile' ? (
                      <UserPhoneIcon />
                    ) : (
                      <PhoneIcon variant="profile" />
                    ))}
                </span>
              );
            }}
          />
        </div>
      </div>
    );
  }
);
